import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PlayerPageAsideLayout } from '@sorare/core/src/components/layout/PlayerPageAsideLayout';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  PlayerPageTabs,
  Tabs,
  Props as TabsProps,
} from '@sorare/core/src/components/seo/Player/Tabs';
import {
  FOOTBALL_PLAYERS_SLUG,
  FOOTBALL_PLAYERS_SLUG_FIXTURES,
  FOOTBALL_PLAYERS_SLUG_HISTORY,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import {
  Content as PlayerMarketDrawerContent,
  Variant as PlayerMarketDrawerContentVariant,
} from '@sorare/marketplace/src/components/player/PlayerMarketDrawer/Content';

import { FootballPlayerHeader } from 'components/player/Header';
import { MobilePlayerPageFooter } from 'components/player/MobilePlayerPageFooter';

import type {
  FootballSeoPlayerLayoutQuery,
  FootballSeoPlayerLayoutQueryVariables,
} from './__generated__/layout.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_SEO_PLAYER_QUERY = gql`
  query FootballSeoPlayerLayoutQuery(
    $slug: String!
    $onlyPrimary: Boolean = false
  ) {
    anyPlayer(slug: $slug) {
      slug
      sport
      displayName
      avatarPictureUrl
      cardSupply {
        limited
      }
      ... on Player {
        slug
        pastGames(first: 1) {
          nodes {
            id
          }
        }
      }

      ...FootballPlayerHeader_anyPlayer
      ...MobilePlayerPageFooter_anyPlayer
    }
  }
  ${FootballPlayerHeader.fragments.anyPlayer}
  ${MobilePlayerPageFooter.fragments.anyPlayer}
` as TypedDocumentNode<
  FootballSeoPlayerLayoutQuery,
  FootballSeoPlayerLayoutQueryVariables
>;

type ContextType = {
  player: FootballSeoPlayerLayoutQuery['anyPlayer'];
  slug: string;
};

export const useLayoutData = () => {
  return useOutletContext<ContextType>();
};

export const loader = queryLoader(
  FOOTBALL_SEO_PLAYER_QUERY,
  ({ slug }: PageParams) => ({ slug })
);

const PlayersSlugLayout = () => {
  const { sport } = useSportContext();
  const { data } = useQueryLoader<FootballSeoPlayerLayoutQuery>();
  const { slug } = useTypedParams<PageParams>();
  const { conversionCredits } = useConversionCredits({
    sport,
  });

  useTitleAndDescription(
    metadatas.playerOverview.title,
    metadatas.playerOverview.description,
    {
      player: data?.anyPlayer.displayName,
    },
    {
      img: data?.anyPlayer.avatarPictureUrl,
    }
  );

  const player = data?.anyPlayer;

  const tabs: TabsProps['tabs'] = useMemo(() => {
    return {
      [PlayerPageTabs.PERFORMANCE]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG, {
          slug,
        }),
      },
      [PlayerPageTabs.FIXTURES]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG_FIXTURES, {
          slug,
        }),
      },
      [PlayerPageTabs.HISTORY]: {
        to: generatePath(FOOTBALL_PLAYERS_SLUG_HISTORY, {
          slug,
        }),
      },
    };
  }, [slug]);

  if (!player || !isType(player, 'Player')) return null;

  const hasEnoughSEOData =
    player.cardSupply.length > 0 || player.pastGames.nodes.length > 0;
  const canUseConversionCredit = conversionCredits?.some(cc => !cc.disabled);

  return (
    <>
      <PageHeader text={player.displayName} />
      {!hasEnoughSEOData && <NoIndex />}
      <PlayerPageAsideLayout
        aside={[
          <PlayerMarketDrawerContent
            key={player.slug}
            playerSlug={player.slug}
            sport={player.sport}
            canUseConversionCredit={canUseConversionCredit}
            featurePrimaryOffer
            withFooter
            variant={PlayerMarketDrawerContentVariant.PLAYER_PAGE}
          />,
        ]}
        footer={<MobilePlayerPageFooter anyPlayer={player} />}
      >
        <Vertical gap={0}>
          <FootballPlayerHeader player={player} />
          <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
            <Outlet context={{ player, slug } satisfies ContextType} />
          </ContentWithStickyHeader>
        </Vertical>
      </PlayerPageAsideLayout>
    </>
  );
};

export default PlayersSlugLayout;
